import { Link, Route, Switch, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import {
  useGetMyTradeProfile,
  useUpdateTradeProfile,
  useCreateTradeProfile,
  useGetLeadPurchases,
  getGetLeadPurchasesQueryKey,
  getGetMyTradeProfileQueryKey,
  useGetReviews,
  getGetReviewsQueryKey,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useCategories } from "@/hooks/use-categories";
import { ImageUpload } from "@/components/ImageUpload";
import { AYRSHIRE_TOWNS } from "@/hooks/use-town";
import { useState, useEffect, useCallback } from "react";
import { usePendingOrders } from "@/context/PendingOrdersContext";
import ShopTermsGate from "@/components/ShopTermsGate";

const TOWNS = AYRSHIRE_TOWNS.filter(t => t !== "All Towns");

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface MyBusiness { id: number; name: string; town: string; approved: boolean; wantsOrdering?: boolean; }

function useMyBusinesses() {
  const [businesses, setBusinesses] = useState<MyBusiness[]>([]);
  useEffect(() => {
    fetch("/api/my-businesses", { headers: authHeaders() })
      .then(r => r.json())
      .then((d: { items: MyBusiness[] }) => setBusinesses(d.items ?? []))
      .catch(() => setBusinesses([]));
  }, []);
  return businesses;
}

function BusinessPickerField({
  businesses, value, onChange, label = "Business *"
}: {
  businesses: MyBusiness[];
  value: string;
  onChange: (name: string, town: string) => void;
  label?: string;
}) {
  const approved = businesses.filter(b => b.approved);
  if (approved.length === 0) {
    return (
      <div className="space-y-1">
        <label className="text-sm font-medium">{label}</label>
        <div className="border border-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-md px-3 py-2 text-sm text-amber-700">
          No approved businesses yet —{" "}
          <Link href="/submit-business" className="font-semibold underline underline-offset-2 hover:text-amber-900 dark:text-amber-300">
            submit a business listing
          </Link>{" "}
          first, then wait for approval.
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      <select
        required
        value={value}
        onChange={e => {
          const biz = approved.find(b => b.name === e.target.value);
          onChange(e.target.value, biz?.town ?? "");
        }}
        className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm"
      >
        <option value="">Select your business</option>
        {approved.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
      </select>
    </div>
  );
}

function BusinessNav() {
  const [location] = useLocation();
  const { user } = useAuth();
  const { hasStore } = usePendingOrders();
  const businesses = useMyBusinesses();

  const isTrade = user?.role === "tradesperson" || user?.role === "admin";
  const isBusiness = user?.role === "business" || user?.role === "admin" || hasStore || businesses.length > 0;

  type NavSection = { label: string; links: { href: string; label: string; icon: string }[] };
  const sections: NavSection[] = [];

  sections.push({
    label: "Overview",
    links: [{ href: "/business", label: "Dashboard", icon: "📊" }],
  });

  if (isBusiness) {
    sections.push({
      label: "My Business",
      links: [
        { href: "/advertise",           label: "Advertise With Us", icon: "📣" },
        { href: "/business/listings",   label: "Business Listings", icon: "🏢" },
        { href: "/business/order-store", label: "Food Ordering",    icon: "🛒" },
        { href: "/business/deals",       label: "Deals & Offers",   icon: "🏷️" },
        { href: "/business/events",      label: "Events",            icon: "🎭" },
        { href: "/business/marketplace", label: "Marketplace",       icon: "🛍️" },
        { href: "/business/reviews",     label: "Reviews",           icon: "⭐" },
      ],
    });
  }

  if (isTrade) {
    sections.push({
      label: "Tradesperson",
      links: [
        { href: "/business/profile", label: "My Trade Profile", icon: "👷" },
        { href: "/business/leads",   label: "Job Leads",        icon: "📋" },
        ...(!isBusiness ? [
          { href: "/business/deals",       label: "Deals & Offers", icon: "🏷️" },
          { href: "/business/events",      label: "Events",          icon: "🎭" },
          { href: "/business/marketplace", label: "Marketplace",     icon: "🛍️" },
        ] : []),
      ],
    });
  }

  return (
    <nav className="flex flex-col gap-4">
      {sections.map((section) => (
        <div key={section.label}>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-1">{section.label}</p>
          {section.links.map(({ href, label, icon }) => (
            <Link
              key={href}
              href={href}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                (href === "/business" ? location === href : location.startsWith(href))
                  ? "bg-primary text-white shadow-sm"
                  : "text-slate-600 hover:text-foreground hover:bg-slate-100"
              }`}
            >
              <span className="text-base leading-none">{icon}</span>
              {label}
            </Link>
          ))}
        </div>
      ))}
    </nav>
  );
}

function BusinessMobileNav() {
  const [location] = useLocation();
  const { user } = useAuth();
  const { hasStore } = usePendingOrders();
  const businesses = useMyBusinesses();

  const isTrade = user?.role === "tradesperson" || user?.role === "admin";
  const isBusiness = user?.role === "business" || user?.role === "admin" || hasStore || businesses.length > 0;

  const links: { href: string; label: string; icon: string }[] = [
    { href: "/business", label: "Dashboard", icon: "📊" },
  ];
  if (isBusiness) {
    links.push(
      { href: "/business/listings",   label: "Listings",    icon: "🏢" },
      { href: "/business/order-store", label: "Food Orders", icon: "🛒" },
      { href: "/business/deals",       label: "Deals",       icon: "🏷️" },
      { href: "/business/events",      label: "Events",      icon: "🎭" },
      { href: "/business/marketplace", label: "Market",      icon: "🛍️" },
      { href: "/business/reviews",     label: "Reviews",     icon: "⭐" },
    );
  }
  if (isTrade) {
    links.push(
      { href: "/business/profile", label: "Profile", icon: "👷" },
      { href: "/business/leads",   label: "Leads",   icon: "📋" },
    );
    if (!isBusiness) {
      links.push(
        { href: "/business/deals",       label: "Deals",  icon: "🏷️" },
        { href: "/business/events",      label: "Events", icon: "🎭" },
        { href: "/business/marketplace", label: "Market", icon: "🛍️" },
      );
    }
  }

  return (
    <div className="md:hidden bg-white border-b border-slate-100 overflow-x-auto">
      <div className="flex gap-1 px-3 py-2 min-w-max">
        {links.map(({ href, label, icon }) => (
          <Link
            key={href}
            href={href}
            className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl text-xs font-medium transition-all whitespace-nowrap ${
              (href === "/business" ? location === href : location.startsWith(href))
                ? "bg-primary text-white"
                : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            <span className="text-base leading-none">{icon}</span>
            {label}
          </Link>
        ))}
      </div>
    </div>
  );
}

function BusinessLayout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { hasStore } = usePendingOrders();
  const roleLabel = user?.role === "tradesperson" ? "Tradesperson" : user?.role === "business" ? "Business Owner" : user?.role === "admin" ? "Admin" : hasStore ? "Store Owner" : "Business";

  return (
    <div className="min-h-screen bg-slate-50">
      <ShopTermsGate />
      <div className="bg-white border-b border-slate-100 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-extrabold text-foreground">Business Portal</h1>
            <p className="text-xs text-slate-400 mt-0.5">{user?.name} · <span className="capitalize">{roleLabel}</span></p>
          </div>
          <Link href="/">
            <button className="text-xs text-slate-400 hover:text-primary transition-colors">← Back to site</button>
          </Link>
        </div>
      </div>
      <BusinessMobileNav />
      <div className="container mx-auto px-4 py-8 flex gap-8">
        <aside className="w-56 flex-shrink-0 hidden md:block">
          <BusinessNav />
        </aside>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}

function StatusBadge({ approved, status, ended }: { approved: boolean; status?: string; ended?: boolean }) {
  if (ended) return <span className="text-xs font-semibold bg-slate-100 text-slate-500 px-2 py-1 rounded-full">Ended</span>;
  if (approved || status === "approved") return <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Live</span>;
  if (status === "rejected") return <span className="text-xs font-semibold bg-red-100 text-red-700 px-2 py-1 rounded-full">Rejected</span>;
  return <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Pending Approval</span>;
}

function BusinessOverview() {
  const { data: profile, isLoading: profileLoading } = useGetMyTradeProfile({ query: { queryKey: getGetMyTradeProfileQueryKey(), staleTime: 60_000 } });
  const { data: leadsData } = useGetLeadPurchases({ query: { queryKey: getGetLeadPurchasesQueryKey() } });

  const [stats, setStats] = useState({ businesses: 0, deals: 0, events: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [orderingBizzes, setOrderingBizzes] = useState<MyBusiness[]>([]);

  useEffect(() => {
    void Promise.all([
      fetch("/api/my-businesses", { headers: authHeaders() }).then(r => r.json() as Promise<{ items: MyBusiness[] }>),
      fetch("/api/my-deals", { headers: authHeaders() }).then(r => r.json() as Promise<{ items: unknown[] }>),
      fetch("/api/my-events", { headers: authHeaders() }).then(r => r.json() as Promise<{ items: unknown[] }>),
    ]).then(([b, d, e]) => {
      const bizList = b.items ?? [];
      setStats({ businesses: bizList.length, deals: d.items?.length ?? 0, events: e.items?.length ?? 0 });
      setOrderingBizzes(bizList.filter(biz => biz.wantsOrdering && biz.approved));
      setStatsLoading(false);
    }).catch(() => setStatsLoading(false));
  }, []);

  const leadCount = leadsData?.total ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-primary">Business Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Your overview at a glance</p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Listings", value: stats.businesses, href: "/business/listings", color: "text-primary" },
          { label: "Leads", value: leadCount, href: "/business/leads", color: "text-primary" },
          { label: "Deals", value: stats.deals, href: "/business/deals", color: "text-secondary" },
          { label: "Events", value: stats.events, href: "/business/events", color: "text-secondary" },
        ].map(({ label, value, href, color }) => (
          <Link key={label} href={href}>
            <Card className="hover:border-primary/40 hover:shadow-md transition-all cursor-pointer">
              <CardContent className="py-5 text-center">
                {statsLoading ? (
                  <div className="h-8 w-10 bg-slate-100 rounded animate-pulse mx-auto mb-1" />
                ) : (
                  <p className={`text-3xl font-bold ${color}`}>{value}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">{label}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Trade profile status */}
      {profileLoading ? (
        <div className="h-20 bg-slate-100 rounded-xl animate-pulse" />
      ) : profile ? (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-semibold text-slate-800">{profile.businessName}</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {[profile.categories?.[0], profile.serviceAreas?.[0]].filter(Boolean).join(" · ")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${profile.verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {profile.verified ? "✓ Verified" : "⏳ Pending Verification"}
                </span>
                <Link href="/business/profile">
                  <Button size="sm" variant="outline">Edit Profile</Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20/40">
          <CardContent className="py-6 text-center">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-3">Set up your trade profile to start receiving leads and listing your services.</p>
            <Link href="/business/profile"><Button className="bg-primary hover:bg-primary/90">Set Up Profile</Button></Link>
          </CardContent>
        </Card>
      )}

      {/* Online ordering CTA */}
      {orderingBizzes.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/40">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0 text-xl">🛒</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm">Food Ordering</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {orderingBizzes.map(b => b.name).join(", ")} — manage your menu, orders & settings
                </p>
              </div>
              <Link href="/business/order-store">
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white shrink-0">Manage</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick actions */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Quick Actions</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Link href="/business/listings">
            <Card className="hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer">
              <CardContent className="py-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-base">🏢</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Business Listings</p>
                  <p className="text-xs text-muted-foreground">Submit & manage your business profiles</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link href="/business/leads">
            <Card className="hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer">
              <CardContent className="py-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-base">📋</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Job Leads</p>
                  <p className="text-xs text-muted-foreground">Browse and unlock quote requests</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link href="/business/deals">
            <Card className="hover:border-secondary/40 hover:shadow-sm transition-all cursor-pointer">
              <CardContent className="py-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-base">🏷️</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Deals & Offers</p>
                  <p className="text-xs text-muted-foreground">Post deals and boost visibility</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link href="/business/events">
            <Card className="hover:border-secondary/40 hover:shadow-sm transition-all cursor-pointer">
              <CardContent className="py-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-base">📅</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Events</p>
                  <p className="text-xs text-muted-foreground">Promote your events to Ayrshire</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <span key={i} className={i <= rating ? "text-amber-400" : "text-slate-200"}>★</span>
      ))}
    </span>
  );
}

interface PortalReview {
  id: number;
  reviewerName: string;
  targetType: string;
  targetId: number;
  rating: number;
  reviewText: string;
  approved: boolean;
  createdAt: string;
}

interface MyStore { id: number; name: string; }

function useMyStores() {
  const [stores, setStores] = useState<MyStore[]>([]);
  useEffect(() => {
    fetch("/api/order/my-stores", { headers: authHeaders() })
      .then(r => r.json())
      .then((d: { stores?: MyStore[] } | MyStore[]) => {
        setStores(Array.isArray(d) ? d : (d.stores ?? []));
      })
      .catch(() => setStores([]));
  }, []);
  return stores;
}

type ReviewTab = "business" | "order_store";

function ReviewList({ reviews, emptyMsg }: { reviews: PortalReview[]; emptyMsg: string }) {
  const approved = reviews.filter(r => r.approved);
  const pending = reviews.filter(r => !r.approved);
  if (reviews.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-2">
          <p className="text-3xl">⭐</p>
          <p className="font-semibold text-slate-700">No reviews yet</p>
          <p className="text-sm text-muted-foreground">{emptyMsg}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {pending.length > 0 && (
        <p className="text-xs text-amber-600 font-medium bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg px-3 py-2">
          {pending.length} review{pending.length > 1 ? "s" : ""} pending approval — not yet visible to the public.
        </p>
      )}
      {reviews.map(r => (
        <Card key={r.id} className={!r.approved ? "opacity-60 border-dashed" : ""}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <StarRating rating={r.rating} />
                  <span className="text-sm font-semibold text-slate-800">{r.reviewerName}</span>
                  {!r.approved && (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded">Pending</span>
                  )}
                </div>
                <p className="text-sm text-slate-600">{r.reviewText}</p>
              </div>
              <p className="text-xs text-slate-400 whitespace-nowrap shrink-0">
                {new Date(r.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
              </p>
            </div>
          </CardContent>
        </Card>
      ))}
      {approved.length > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          Average: {(approved.reduce((s, r) => s + r.rating, 0) / approved.length).toFixed(1)} / 5 from {approved.length} approved review{approved.length !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}

function MyReviews() {
  const businesses = useMyBusinesses();
  const stores = useMyStores();
  const [tab, setTab] = useState<ReviewTab>("business");
  const [allReviews, setAllReviews] = useState<PortalReview[]>([]);
  const [loading, setLoading] = useState(false);

  const bizIds = businesses.map(b => b.id);
  const storeIds = stores.map(s => s.id);

  useEffect(() => {
    const ids = tab === "business" ? bizIds : storeIds;
    if (ids.length === 0) { setAllReviews([]); return; }
    setLoading(true);
    Promise.all(
      ids.map(id =>
        fetch(`/api/reviews?targetType=${tab}&targetId=${id}`, { headers: authHeaders() })
          .then(r => r.json() as Promise<{ items: PortalReview[] }>)
          .then(d => d.items ?? [])
          .catch(() => [] as PortalReview[])
      )
    ).then(results => {
      setAllReviews(results.flat().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    }).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, bizIds.join(","), storeIds.join(",")]);

  const hasBusiness = businesses.length > 0;
  const hasStore = stores.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-primary">My Reviews</h1>
        <p className="text-sm text-slate-500 mt-1">Customer reviews across your business listings and food ordering shops.</p>
      </div>

      {/* Tab switcher — only show tabs that apply */}
      {(hasBusiness || hasStore) && (
        <div className="flex gap-2 border-b border-slate-100 pb-3">
          {hasBusiness && (
            <button
              onClick={() => setTab("business")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                tab === "business" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary"
              }`}
            >
              🏢 Business Listings
            </button>
          )}
          {hasStore && (
            <button
              onClick={() => setTab("order_store")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                tab === "order_store" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary"
              }`}
            >
              🛒 Food & Ordering
            </button>
          )}
        </div>
      )}

      {!hasBusiness && !hasStore ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <p className="text-4xl">⭐</p>
            <p className="font-semibold text-slate-700">No businesses or shops yet</p>
            <p className="text-sm text-muted-foreground">Submit a business listing to start receiving reviews.</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <p className="text-muted-foreground text-sm">Loading reviews…</p>
      ) : (
        <ReviewList
          reviews={allReviews}
          emptyMsg={tab === "business"
            ? "Reviews will appear here once customers leave feedback on your business page."
            : "Reviews will appear here once customers leave feedback on your shop."}
        />
      )}
    </div>
  );
}

interface FullBusiness {
  id: number; name: string; category: string; description: string | null;
  town: string | null; address: string | null; phone: string | null;
  email: string | null; website: string | null; facebook: string | null;
  instagram: string | null; openingHours: string | null; imageUrl: string | null;
  approved: boolean;
}

function MyBusinessListings() {
  const businesses = useMyBusinesses();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<FullBusiness>>({});
  const [editSaving, setEditSaving] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [refetchCount, setRefetchCount] = useState(0);
  void refetchCount;

  const startEdit = async (biz: MyBusiness) => {
    setEditLoading(true);
    try {
      const res = await fetch(`/api/businesses/${biz.id}`, { headers: authHeaders() });
      const data = await res.json() as FullBusiness;
      setEditForm({ name: data.name, category: data.category, description: data.description ?? "", town: data.town ?? "", address: data.address ?? "", phone: data.phone ?? "", email: data.email ?? "", website: data.website ?? "", facebook: data.facebook ?? "", instagram: data.instagram ?? "", openingHours: data.openingHours ?? "", imageUrl: data.imageUrl ?? "" });
      setEditingId(biz.id);
    } finally { setEditLoading(false); }
  };

  const handleSave = async (id: number) => {
    setEditSaving(true);
    try {
      const res = await fetch(`/api/businesses/${id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(editForm) });
      if (!res.ok) { const d = await res.json() as { error?: string }; throw new Error(d.error ?? "Save failed"); }
      setEditingId(null);
      setRefetchCount(c => c + 1);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Save failed");
    } finally { setEditSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-primary">My Businesses</h1>
          <p className="text-sm text-slate-500 mt-1">Submit and manage your business directory listings.</p>
        </div>
        <Button asChild className="bg-primary hover:bg-primary/90">
          <Link href="/submit-business">+ Submit Business</Link>
        </Button>
      </div>

      {businesses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-4">
            <p className="text-4xl">🏪</p>
            <p className="font-semibold text-slate-700">No business listings yet</p>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              Submit your business to appear in the Ayrshire Hub directory. Once approved, you can post deals and events.
            </p>
            <Button asChild className="bg-primary hover:bg-primary/90">
              <Link href="/submit-business">Submit Your Business</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {businesses.map(biz => (
            <Card key={biz.id}>
              <CardContent className="py-4">
                {editingId === biz.id ? (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm text-slate-700">Edit Business Listing</h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1 sm:col-span-2"><label className="text-xs font-medium text-slate-600">Business Name *</label><Input value={editForm.name ?? ""} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Town</label><Input value={editForm.town ?? ""} onChange={e => setEditForm(f => ({ ...f, town: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Phone</label><Input type="tel" value={editForm.phone ?? ""} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Email</label><Input type="email" value={editForm.email ?? ""} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Website</label><Input type="url" placeholder="https://…" value={editForm.website ?? ""} onChange={e => setEditForm(f => ({ ...f, website: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Address</label><Input value={editForm.address ?? ""} onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Facebook</label><Input placeholder="https://facebook.com/…" value={editForm.facebook ?? ""} onChange={e => setEditForm(f => ({ ...f, facebook: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Instagram</label><Input placeholder="@handle or URL" value={editForm.instagram ?? ""} onChange={e => setEditForm(f => ({ ...f, instagram: e.target.value }))} /></div>
                      <div className="space-y-1 sm:col-span-2"><label className="text-xs font-medium text-slate-600">Description</label><Textarea rows={3} value={editForm.description ?? ""} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} /></div>
                      <div className="space-y-1 sm:col-span-2"><label className="text-xs font-medium text-slate-600">Opening Hours</label><Textarea rows={2} placeholder="e.g. Mon–Fri 9am–5pm" value={editForm.openingHours ?? ""} onChange={e => setEditForm(f => ({ ...f, openingHours: e.target.value }))} /></div>
                    </div>
                    <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Photo</label><ImageUpload value={editForm.imageUrl ?? ""} onChange={url => setEditForm(f => ({ ...f, imageUrl: url }))} label="Business Photo" stockCategory="business" /></div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={() => void handleSave(biz.id)} disabled={editSaving}>{editSaving ? "Saving…" : "Save Changes"}</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-semibold text-slate-800">{biz.name}</p>
                      <p className="text-sm text-slate-500 mt-0.5">{biz.town}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {biz.approved ? (
                        <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">✓ Live</span>
                      ) : (
                        <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Pending Approval</span>
                      )}
                      <button
                        onClick={() => void startEdit(biz)}
                        disabled={editLoading}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 hover:border-blue-400 px-2 py-1 rounded transition-colors disabled:opacity-50"
                      >
                        {editLoading ? "Loading…" : "Edit"}
                      </button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          <div className="pt-2">
            <Button asChild variant="outline">
              <Link href="/submit-business">+ Submit Another Business</Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function BusinessProfile() {
  const { data: profile, isLoading, refetch } = useGetMyTradeProfile({ query: { queryKey: getGetMyTradeProfileQueryKey(), staleTime: 60_000 } });
  const updateProfile = useUpdateTradeProfile();
  const createProfile = useCreateTradeProfile();
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({ businessName: "", serviceArea: "", description: "", phone: "" });
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoriesInitialised, setCategoriesInitialised] = useState(false);
  const tradeCategoryList = useCategories("job");
  const handleChange = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (profile && !categoriesInitialised) {
      setSelectedCategories(profile.categories ?? []);
      setCategoriesInitialised(true);
    }
  }, [profile, categoriesInitialised]);

  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const isPending = updateProfile.isPending || createProfile.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const businessName = form.businessName || profile?.businessName || "";
    const categories = selectedCategories.length > 0 ? selectedCategories : (profile?.categories ?? []);
    const serviceAreas = form.serviceArea ? [form.serviceArea] : (profile?.serviceAreas ?? [TOWNS[0]!]);
    const description = form.description || profile?.description || "";
    const phone = form.phone || profile?.phone || "";

    if (profile?.id) {
      await updateProfile.mutateAsync({
        id: profile.id,
        data: { businessName, categories, serviceAreas, description, phone, emergencyCallout: profile.emergencyCallout },
      });
    } else {
      await createProfile.mutateAsync({
        data: { businessName, categories, serviceAreas, description, phone },
      });
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    await refetch();
  };

  const pv = (field: string) => (form as Record<string, string>)[field] || (profile as unknown as Record<string, string>)?.[field] || "";

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-primary">Trade Profile</h1>
      {isLoading ? (
        <div className="space-y-4">
          <div className="h-10 bg-slate-100 rounded-lg animate-pulse w-1/3" />
          <div className="rounded-xl border border-slate-100 p-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {[1,2,3].map(i => <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />)}
            </div>
            <div className="h-24 bg-slate-100 rounded-lg animate-pulse" />
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Business Name</label>
                  <Input required value={pv("businessName")} onChange={e => handleChange("businessName", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Based in</label>
                  <select value={form.serviceArea || profile?.serviceAreas?.[0] || TOWNS[0]} onChange={e => handleChange("serviceArea", e.target.value)} className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm">
                    {TOWNS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Phone</label>
                  <Input value={pv("phone")} onChange={e => handleChange("phone", e.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <label className="text-sm font-medium">Your Trades</label>
                  <p className="text-xs text-muted-foreground mt-0.5">Select all that apply — you'll receive lead notifications for each</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {tradeCategoryList.map(cat => {
                    const active = selectedCategories.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        className="px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                        style={{
                          backgroundColor: active ? "#d97706" : "var(--color-card)",
                          borderColor: active ? "#d97706" : "#e2e8f0",
                          color: active ? "var(--color-card)" : "#64748b",
                        }}
                      >
                        {active && "✓ "}{cat}
                      </button>
                    );
                  })}
                </div>
                {selectedCategories.length > 0 && (
                  <p className="text-xs text-amber-700 font-medium">
                    {selectedCategories.length} trade{selectedCategories.length > 1 ? "s" : ""} selected
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Description</label>
                <Textarea rows={4} value={pv("description")} onChange={e => handleChange("description", e.target.value)} />
              </div>
              <div className="flex gap-3 items-center">
                <Button type="submit" disabled={isPending}>
                  {isPending ? "Saving..." : profile?.id ? "Save Changes" : "Create Profile"}
                </Button>
                {saved && <span className="text-sm text-emerald-600 font-medium">Saved!</span>}
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface AvailableLead {
  id: number; title: string; description: string; category: string; town: string;
  urgency: string; budgetRange: string | null; leadPrice: number | null;
  contactName: string | null; contactPhone: string | null; contactEmail: string | null;
  unlockedByMe: boolean; conversationId: number | null; createdAt: string;
}

function BusinessLeads() {
  const { toast } = useToast();
  const [items, setItems] = useState<AvailableLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(true);
  const [unlocking, setUnlocking] = useState<number | null>(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/leads/available", { headers: authHeaders() });
      if (res.status === 401) { setItems([]); setLoading(false); return; }
      const data = await res.json() as { items: AvailableLead[]; hasProfile?: boolean };
      setHasProfile(data.hasProfile !== false);
      setItems(data.items ?? []);
    } catch {
      toast({ title: "Failed to load leads", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { void fetchLeads(); }, [fetchLeads]);

  // Handle return from Stripe for lead unlock
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const jobId = params.get("jobId");
    if (params.get("payment_success") === "1" && params.get("type") === "lead" && sessionId && jobId) {
      window.history.replaceState({}, "", window.location.pathname);
      void fetch(`/api/jobs/${jobId}/unlock`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ stripeSessionId: sessionId }),
      }).then(async r => {
        if (r.ok) {
          toast({ title: "Lead unlocked! Contact details are now visible." });
          await fetchLeads();
        } else {
          const err = await r.json() as { error?: string };
          toast({ title: err.error ?? "Failed to unlock lead", variant: "destructive" });
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnlock = async (job: AvailableLead) => {
    const price = job.leadPrice ?? 0;
    setUnlocking(job.id);
    try {
      if (price > 0) {
        const res = await fetch("/api/stripe/checkout", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            amount: price,
            description: `Lead Unlock: ${job.title} — Ayrshire Hub`,
            successUrl: `${window.location.origin}${window.location.pathname}?payment_success=1&type=lead&jobId=${job.id}&session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: window.location.href,
            metadata: { type: "lead", jobId: String(job.id) },
          }),
        });
        const data = await res.json() as { url?: string; error?: string };
        if (data.url) { window.location.href = data.url; return; }
        throw new Error(data.error ?? "Payment setup failed");
      }
      // Free unlock
      const r = await fetch(`/api/jobs/${job.id}/unlock`, { method: "POST", headers: authHeaders(), body: JSON.stringify({}) });
      if (r.ok) {
        toast({ title: "Lead unlocked!" });
        await fetchLeads();
      } else {
        const err = await r.json() as { error?: string };
        toast({ title: err.error ?? "Failed to unlock", variant: "destructive" });
      }
    } catch {
      toast({ title: "Something went wrong", variant: "destructive" });
    } finally { setUnlocking(null); }
  };

  const urgencyLabel = (u: string) => ({ emergency: "🚨 Emergency", this_week: "This Week", flexible: "Flexible" }[u] ?? u);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-primary">Lead Enquiries</h1>
        <p className="text-sm text-slate-500 mt-1">All active quote requests in your trade categories across Ayrshire.</p>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading leads…</div>
      ) : !hasProfile ? (
        <Card><CardContent className="py-10 text-center">
          <p className="text-muted-foreground mb-3">Set up your trade profile first to see leads in your categories.</p>
          <Button asChild><Link href="/business/profile">Set Up Profile</Link></Button>
        </CardContent></Card>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center">
          <p className="text-muted-foreground">No active leads in your categories right now. Check back soon — you'll receive a notification when new ones come in.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {items.map(job => (
            <Card key={job.id} className={job.unlockedByMe ? "border-emerald-200 bg-emerald-50/30" : ""}>
              <CardContent className="py-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-slate-800">{job.title}</p>
                      {job.unlockedByMe && <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">✓ Unlocked</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap mb-2">
                      <span>{job.town}</span>
                      <span>·</span>
                      <span>{job.category}</span>
                      <span>·</span>
                      <span>{urgencyLabel(job.urgency)}</span>
                      {job.budgetRange && <><span>·</span><span>{job.budgetRange}</span></>}
                      <span>·</span>
                      <span>{new Date(job.createdAt).toLocaleDateString("en-GB")}</span>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{job.description}</p>

                    {job.unlockedByMe && (
                      <div className="mt-3 p-3 bg-white border border-emerald-200 rounded-lg space-y-1">
                        {job.contactName && <p className="text-sm font-medium text-slate-800">{job.contactName}</p>}
                        {job.contactPhone && (
                          <a href={`tel:${job.contactPhone}`} className="flex items-center gap-1.5 text-sm text-primary font-semibold hover:underline">
                            📞 {job.contactPhone}
                          </a>
                        )}
                        {job.contactEmail && (
                          <a href={`mailto:${job.contactEmail}`} className="flex items-center gap-1.5 text-sm text-primary font-semibold hover:underline">
                            ✉ {job.contactEmail}
                          </a>
                        )}
                        <div className="pt-1">
                          <a
                            href={job.conversationId
                              ? `/messages?conversationId=${job.conversationId}`
                              : `/messages?jobId=${job.id}`}
                            className="inline-flex items-center gap-1.5 text-sm bg-primary text-white font-semibold px-3 py-1.5 rounded-lg hover:bg-primary/90 transition-colors"
                          >
                            💬 Message Resident
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  {!job.unlockedByMe && (
                    <div className="flex-shrink-0 text-right">
                      <p className="text-xs text-slate-400 mb-1">Lead fee</p>
                      <p className="text-lg font-bold text-primary mb-2">
                        {(job.leadPrice ?? 0) === 0 ? "Free" : `£${(job.leadPrice ?? 0).toFixed(2)}`}
                      </p>
                      <Button
                        onClick={() => void handleUnlock(job)}
                        disabled={unlocking === job.id}
                        size="sm"
                        className="bg-primary hover:bg-primary/90"
                      >
                        {unlocking === job.id ? "Processing…" : (job.leadPrice ?? 0) === 0 ? "Unlock Free" : "Pay & Unlock"}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

interface Deal {
  id: number; title: string; businessName: string; description: string;
  town: string; category: string; imageUrl?: string | null;
  startDate?: string | null; endDate?: string | null; approved: boolean; createdAt: string;
  sponsored?: boolean; sponsoredUntil?: string | null;
  redemptions: { id: number; userName: string | null; redeemedAt: string }[];
}

function MyDeals() {
  const dealCats = useCategories("deal");
  const myBusinesses = useMyBusinesses();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    title: "", businessName: "", description: "", category: "", town: "Ayr",
    discount: "", startDate: "", endDate: "", redemptionInstructions: "", imageUrl: "",
  });
  const [dealSponsorWeeks, setDealSponsorWeeks] = useState<1|2|4|null>(null);
  const [dealBoostPrices, setDealBoostPrices] = useState<Record<number, number>>({});
  const [dealListingFee, setDealListingFee] = useState<number>(0);
  const [dealBoostLoading, setDealBoostLoading] = useState(false);
  const [dealEditingId, setDealEditingId] = useState<number | null>(null);
  const [dealEditForm, setDealEditForm] = useState({ title: "", businessName: "", description: "", category: "", town: "Ayr", discount: "", startDate: "", endDate: "", redemptionInstructions: "", imageUrl: "" });
  const [dealEditSaving, setDealEditSaving] = useState(false);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/my-deals", { headers: authHeaders() });
      const data = await res.json() as { items: Deal[] };
      setDeals((data.items ?? []).map(deal => ({ ...deal, redemptions: deal.redemptions ?? [] })));
    } catch { setDeals([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  useEffect(() => {
    if (!showForm) return;
    setDealBoostLoading(true);
    void Promise.all([
      fetch("/api/stripe/price?type=deal", { headers: authHeaders() }).then(r => r.json() as Promise<{ price: number }>),
      ...([1, 2, 4] as const).map(w =>
        fetch(`/api/stripe/price?type=sponsored_deal_${w}w`, { headers: authHeaders() })
          .then(r => r.json() as Promise<{ price: number }>)
          .then(d => [w, d.price] as const)
      ),
    ]).then(([listingRes, ...boostResults]) => {
      setDealListingFee((listingRes as { price: number }).price);
      setDealBoostPrices(Object.fromEntries(boostResults as [number, number][]));
      setDealBoostLoading(false);
    });
  }, [showForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const [listingData, ...boostArr] = await Promise.all([
        fetch("/api/stripe/price?type=deal", { headers: authHeaders() }).then(r => r.json() as Promise<{ price: number; isFree: boolean }>),
        ...(dealSponsorWeeks ? [
          fetch(`/api/stripe/price?type=sponsored_deal_${dealSponsorWeeks}w`, { headers: authHeaders() }).then(r => r.json() as Promise<{ price: number }>)
        ] : []),
      ]);
      const listingFee = listingData.price;
      const boostFee = dealSponsorWeeks ? (boostArr[0]?.price ?? 0) : 0;
      const totalAmount = listingFee + boostFee;

      if (totalAmount > 0) {
        sessionStorage.setItem("pending_deal_form", JSON.stringify({ ...form, sponsorWeeks: dealSponsorWeeks }));
        const checkoutRes = await fetch("/api/stripe/checkout", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            amount: totalAmount / 100,
            description: dealSponsorWeeks
              ? `Deal Listing + ${dealSponsorWeeks}w Featured Boost — Ayrshire Hub`
              : "Deal Listing Fee — Ayrshire Hub",
            successUrl: `${window.location.origin}${window.location.pathname}?payment_success=1&type=deal&session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: window.location.href,
            metadata: { type: "deal", ...(dealSponsorWeeks ? { sponsorWeeks: String(dealSponsorWeeks) } : {}) },
          }),
        });
        const checkout = await checkoutRes.json() as { url?: string; error?: string };
        if (checkout.url) { window.location.href = checkout.url; return; }
        throw new Error(checkout.error ?? "Payment setup failed");
      }
      const dealRes = await fetch("/api/deals", { method: "POST", headers: authHeaders(), body: JSON.stringify({ ...form, sponsorWeeks: dealSponsorWeeks }) });
      if (!dealRes.ok) { const d = await dealRes.json() as { error?: string }; throw new Error(d.error ?? "Failed to submit deal"); }
      setDone(true);
      setShowForm(false);
      setForm({ title: "", businessName: "", description: "", category: "", town: "", discount: "", startDate: "", endDate: "", redemptionInstructions: "", imageUrl: "" });
      setDealSponsorWeeks(null);
      await fetchDeals();
    } finally { setSubmitting(false); }
  };

  // Handle return from Stripe payment for deals
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("payment_success") === "1" && sessionId && params.get("type") === "deal") {
      const savedForm = sessionStorage.getItem("pending_deal_form");
      if (savedForm) {
        const formData = JSON.parse(savedForm) as typeof form & { sponsorWeeks?: 1|2|4|null };
        sessionStorage.removeItem("pending_deal_form");
        window.history.replaceState({}, "", window.location.pathname);
        void fetch("/api/deals", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ ...formData, stripeSessionId: sessionId }),
        }).then(() => { setDone(true); void fetchDeals(); });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEndEarly = async (id: number) => {
    if (!confirm("End this deal early? It will no longer appear publicly.")) return;
    await fetch(`/api/deals/${id}/end`, { method: "PATCH", headers: authHeaders() });
    await fetchDeals();
  };

  const startEditDeal = (deal: Deal) => {
    setDealEditForm({ title: deal.title, businessName: deal.businessName, description: deal.description ?? "", category: deal.category ?? "", town: deal.town ?? "Ayr", discount: "", startDate: deal.startDate ?? "", endDate: deal.endDate ?? "", redemptionInstructions: "", imageUrl: deal.imageUrl ?? "" });
    setDealEditingId(deal.id);
  };

  const handleSaveDealEdit = async (id: number) => {
    setDealEditSaving(true);
    try {
      const res = await fetch(`/api/deals/${id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(dealEditForm) });
      const data = await res.json() as { requiresReapproval?: boolean; error?: string };
      if (!res.ok) throw new Error(data.error ?? "Failed to save");
      if (data.requiresReapproval) alert("Deal updated. As it was live, it has been sent for re-approval.");
      setDealEditingId(null);
      await fetchDeals();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Save failed");
    } finally { setDealEditSaving(false); }
  };

  const isDealEnded = (deal: Deal) => {
    if (!deal.endDate) return false;
    return new Date(deal.endDate) < new Date();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-primary">My Deals</h1>
        <Button onClick={() => setShowForm(!showForm)} className="bg-primary hover:bg-primary/90">
          {showForm ? "Cancel" : "+ New Deal"}
        </Button>
      </div>

      {done && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg text-sm font-medium">
          Deal submitted for approval. You'll be notified once it's reviewed.
        </div>
      )}

      {showForm && (
        <Card>
          <CardContent className="pt-6">
            <h2 className="font-semibold text-slate-800 mb-4">Submit New Deal</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Deal Title *</label>
                  <Input required placeholder="e.g. 20% off all boiler services" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                </div>
                <BusinessPickerField
                  businesses={myBusinesses}
                  value={form.businessName}
                  onChange={(name, town) => setForm(f => ({ ...f, businessName: name, town: town || f.town }))}
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium">Category *</label>
                  <select required value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm">
                    <option value="">Select category</option>
                    {dealCats.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Town *</label>
                  <select required value={form.town} onChange={e => setForm(f => ({ ...f, town: e.target.value }))} className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm">
                    {TOWNS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Discount / Offer</label>
                  <Input placeholder="e.g. 20% off" value={form.discount} onChange={e => setForm(f => ({ ...f, discount: e.target.value }))} />
                </div>
                <div className="space-y-1" />
                <div className="space-y-1">
                  <label className="text-sm font-medium">Start Date</label>
                  <Input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">End Date</label>
                  <Input type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Description *</label>
                <Textarea required rows={3} placeholder="Describe the deal terms and conditions..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Redemption Instructions</label>
                <Input placeholder="e.g. Show this page in store" value={form.redemptionInstructions} onChange={e => setForm(f => ({ ...f, redemptionInstructions: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Deal Image</label>
                <ImageUpload value={form.imageUrl} onChange={url => setForm(f => ({ ...f, imageUrl: url }))} label="Upload Deal Image" stockCategory="deal" />
              </div>
              <div className="border border-amber-200 bg-amber-50 dark:bg-amber-900/20/60 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">⭐ Optional: Featured Boost</p>
                  <p className="text-xs text-amber-700 mt-0.5">Pin your deal to the top of listings for extra visibility.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {([null, 1, 2, 4] as const).map(w => {
                    const boostPrice = w === null ? 0 : (dealBoostPrices[w] ?? 0);
                    const label = w === null ? "No boost" : w === 1 ? "1 Week" : w === 2 ? "2 Weeks" : "4 Weeks";
                    const boostLabel = dealBoostLoading ? "…" : w === null ? "" : boostPrice === 0 ? " · Free" : ` · £${(boostPrice / 100).toFixed(2)}`;
                    const active = dealSponsorWeeks === w;
                    return (
                      <button
                        key={String(w)}
                        type="button"
                        onClick={() => setDealSponsorWeeks(w)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${active ? (w === null ? "bg-slate-700 text-white border-slate-700" : "bg-amber-500 text-white border-amber-500") : "bg-white border-amber-200 text-amber-800 dark:text-amber-300 hover:border-amber-400"}`}
                      >
                        {label}{boostLabel}
                      </button>
                    );
                  })}
                </div>
                {!dealBoostLoading && (
                  <div className="text-xs text-amber-800 dark:text-amber-300 border-t border-amber-200 pt-2 space-y-0.5">
                    {dealListingFee > 0 && <div className="flex justify-between"><span>Listing fee</span><span className="font-medium">£{(dealListingFee / 100).toFixed(2)}</span></div>}
                    {dealSponsorWeeks && (dealBoostPrices[dealSponsorWeeks] ?? 0) > 0 && <div className="flex justify-between"><span>Boost ({dealSponsorWeeks}w)</span><span className="font-medium">£{((dealBoostPrices[dealSponsorWeeks] ?? 0) / 100).toFixed(2)}</span></div>}
                    {(dealListingFee > 0 || (dealSponsorWeeks && (dealBoostPrices[dealSponsorWeeks] ?? 0) > 0)) && (
                      <div className="flex justify-between font-semibold border-t border-amber-300 pt-1 mt-1">
                        <span>Total due today</span>
                        <span>£{((dealListingFee + (dealSponsorWeeks ? (dealBoostPrices[dealSponsorWeeks] ?? 0) : 0)) / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {dealListingFee === 0 && (!dealSponsorWeeks || (dealBoostPrices[dealSponsorWeeks] ?? 0) === 0) && (
                      <div className="text-emerald-700 font-medium">Free to submit</div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary/90">
                  {submitting ? "Submitting…" : dealSponsorWeeks ? "Submit + Boost" : "Submit for Approval"}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setDealSponsorWeeks(null); }}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="space-y-3">{[1, 2].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      ) : deals.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">No deals yet. Click "+ New Deal" to submit your first offer.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {deals.map(deal => (
            <Card key={deal.id}>
              <CardContent className="py-4">
                {dealEditingId === deal.id ? (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm text-slate-700">Edit Deal</h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1 sm:col-span-2"><label className="text-xs font-medium text-slate-600">Title *</label><Input value={dealEditForm.title} onChange={e => setDealEditForm(f => ({ ...f, title: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Business Name</label><Input value={dealEditForm.businessName} onChange={e => setDealEditForm(f => ({ ...f, businessName: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Category</label><select value={dealEditForm.category} onChange={e => setDealEditForm(f => ({ ...f, category: e.target.value }))} className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm"><option value="">Select</option>{dealCats.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Town</label><select value={dealEditForm.town} onChange={e => setDealEditForm(f => ({ ...f, town: e.target.value }))} className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm">{TOWNS.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Starts</label><Input type="date" value={dealEditForm.startDate} onChange={e => setDealEditForm(f => ({ ...f, startDate: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Ends</label><Input type="date" value={dealEditForm.endDate} onChange={e => setDealEditForm(f => ({ ...f, endDate: e.target.value }))} /></div>
                      <div className="space-y-1 sm:col-span-2"><label className="text-xs font-medium text-slate-600">Description</label><Textarea rows={2} value={dealEditForm.description} onChange={e => setDealEditForm(f => ({ ...f, description: e.target.value }))} /></div>
                    </div>
                    <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Image</label><ImageUpload value={dealEditForm.imageUrl} onChange={url => setDealEditForm(f => ({ ...f, imageUrl: url }))} label="Deal Image" stockCategory="deal" /></div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={() => void handleSaveDealEdit(deal.id)} disabled={dealEditSaving}>{dealEditSaving ? "Saving…" : "Save Changes"}</Button>
                      <Button size="sm" variant="outline" onClick={() => setDealEditingId(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3 items-start">
                    {deal.imageUrl && (
                      <img src={deal.imageUrl} alt={deal.title} className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <p className="font-semibold text-sm sm:text-base truncate">{deal.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{deal.category} · {deal.town}</p>
                          {(deal.startDate || deal.endDate) && (
                            <p className="text-xs text-slate-400 mt-0.5">
                              {deal.startDate && `From ${new Date(deal.startDate).toLocaleDateString("en-GB")}`}
                              {deal.startDate && deal.endDate && " · "}
                              {deal.endDate && `Until ${new Date(deal.endDate).toLocaleDateString("en-GB")}`}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                          {deal.sponsored && (
                            <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1">
                              ⭐ Sponsored
                              {deal.sponsoredUntil && <span className="font-normal opacity-80">until {new Date(deal.sponsoredUntil).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>}
                            </span>
                          )}
                          <StatusBadge approved={deal.approved} ended={isDealEnded(deal)} />
                          {!isDealEnded(deal) && (
                            <button onClick={() => startEditDeal(deal)} className="text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 hover:border-blue-400 px-2 py-1 rounded transition-colors">Edit</button>
                          )}
                          {deal.approved && !isDealEnded(deal) && !deal.sponsored && (
                            <SponsorButton type="deal" id={deal.id} onDone={fetchDeals} />
                          )}
                          {!isDealEnded(deal) && (
                            <button
                              onClick={() => handleEndEarly(deal.id)}
                              className="text-xs text-red-500 hover:text-red-700 font-medium border border-red-200 hover:border-red-400 px-2 py-1 rounded transition-colors"
                            >
                              End Early
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 border-t border-slate-100 pt-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                          Redemptions ({deal.redemptions.length})
                        </p>
                        {deal.redemptions.length === 0 ? (
                          <p className="text-sm text-slate-400">No one has redeemed this deal yet.</p>
                        ) : (
                          <div className="max-h-40 overflow-y-auto divide-y divide-slate-100 rounded-lg border border-slate-100">
                            {deal.redemptions.map(redemption => (
                              <div key={redemption.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                                <span className="font-medium text-slate-700">{redemption.userName ?? "Deleted account"}</span>
                                <span className="text-xs text-slate-400 whitespace-nowrap">
                                  {new Date(redemption.redeemedAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

interface Event {
  id: number; name: string; description: string; category: string;
  town: string; venue?: string | null; eventDate: string; eventTime?: string | null;
  price?: string | null; imageUrl?: string | null; bookingLink?: string | null;
  organiserName?: string | null; approved: boolean; status?: string | null; createdAt: string;
  sponsored?: boolean; sponsoredUntil?: string | null;
}

const SPONSOR_WEEKS = [
  { weeks: 1, label: "1 Week" },
  { weeks: 2, label: "2 Weeks" },
  { weeks: 4, label: "4 Weeks" },
] as const;

type SponsorType = "deal" | "event" | "free-item";
const SPONSOR_PATH: Record<SponsorType, string> = {
  "deal": "deals",
  "event": "events",
  "free-item": "free-items",
};
const SPONSOR_FEE_PREFIX: Record<SponsorType, string> = {
  "deal": "sponsored_deal",
  "event": "sponsored_event",
  "free-item": "sponsored_free_item",
};

function SponsorButton({ type, id, onDone }: { type: SponsorType; id: number; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [prices, setPrices] = useState<Record<number, number>>({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [sponsoring, setSponsoring] = useState<number | null>(null);

  const loadPrices = async () => {
    setLoadingPrices(true);
    try {
      const results = await Promise.all(
        [1, 2, 4].map(async (w) => {
          const res = await fetch(`/api/stripe/price?type=${SPONSOR_FEE_PREFIX[type]}_${w}w`, { headers: authHeaders() });
          const data = await res.json() as { price: number };
          return [w, data.price] as const;
        })
      );
      setPrices(Object.fromEntries(results));
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleOpen = () => { setOpen(true); void loadPrices(); };

  const handleSponsor = async (weeks: number) => {
    setSponsoring(weeks);
    try {
      const price = prices[weeks] ?? 0;
      if (price > 0) {
        sessionStorage.setItem(`pending_sponsor_${type}_${id}`, JSON.stringify({ weeks, type, id }));
        const checkoutRes = await fetch("/api/stripe/checkout", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            amount: price / 100,
            description: `Sponsored ${type} — ${weeks} week${weeks > 1 ? "s" : ""} — Ayrshire Hub`,
            successUrl: `${window.location.origin}${window.location.pathname}?sponsor_success=1&sponsor_type=${type}&sponsor_id=${id}&sponsor_weeks=${weeks}&session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: window.location.href,
            metadata: { type: `${SPONSOR_FEE_PREFIX[type]}_${weeks}w`, sponsorType: type, sponsorId: String(id) },
          }),
        });
        const checkout = await checkoutRes.json() as { url?: string; error?: string };
        if (checkout.url) { window.location.href = checkout.url; return; }
        throw new Error(checkout.error ?? "Payment setup failed");
      }
      // Free sponsorship — call API directly
      await fetch(`/api/${SPONSOR_PATH[type]}/${id}/sponsor`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ weeks }),
      });
      setOpen(false);
      onDone();
    } finally {
      setSponsoring(null);
    }
  };

  // Handle return from Stripe for sponsorship
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("sponsor_success") === "1") {
      const sponsorType = params.get("sponsor_type");
      const sponsorId = params.get("sponsor_id");
      const weeks = params.get("sponsor_weeks");
      const sessionId = params.get("session_id");
      if (sponsorType === type && sponsorId === String(id) && weeks && sessionId) {
        window.history.replaceState({}, "", window.location.pathname);
        void fetch(`/api/${SPONSOR_PATH[type]}/${id}/sponsor`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ weeks: Number(weeks), stripeSessionId: sessionId }),
        }).then(() => onDone());
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open) {
    return (
      <button
        onClick={handleOpen}
        className="text-xs font-semibold text-amber-700 border border-amber-300 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 px-2 py-1 rounded transition-colors flex items-center gap-1"
      >
        ⭐ Boost
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1 border border-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 min-w-[200px]">
      <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">⭐ Sponsor this {type}</p>
      {loadingPrices ? (
        <p className="text-xs text-slate-500">Loading prices…</p>
      ) : (
        <div className="flex flex-col gap-1">
          {SPONSOR_WEEKS.map(({ weeks, label }) => (
            <button
              key={weeks}
              onClick={() => void handleSponsor(weeks)}
              disabled={sponsoring !== null}
              className="flex items-center justify-between text-xs font-medium bg-white border border-amber-200 hover:border-amber-400 hover:bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              <span>{label}</span>
              <span className="text-amber-700 font-semibold">
                {(prices[weeks] ?? 0) === 0 ? "Free" : `£${((prices[weeks] ?? 0) / 100).toFixed(2)}`}
              </span>
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => setOpen(false)}
        className="text-xs text-slate-500 hover:text-slate-700 mt-1 text-left"
      >
        Cancel
      </button>
    </div>
  );
}

function MyEvents() {
  const { toast } = useToast();
  const eventCats = useCategories("event");
  const myBusinesses = useMyBusinesses();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", category: "", town: "Ayr", venue: "",
    eventDate: "", eventTime: "", price: "", bookingLink: "",
    organiserName: "", imageUrl: "",
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", category: "", town: "Ayr", venue: "", eventDate: "", eventTime: "", price: "", bookingLink: "", organiserName: "", imageUrl: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [eventSponsorWeeks, setEventSponsorWeeks] = useState<1|2|4|null>(null);
  const [eventBoostPrices, setEventBoostPrices] = useState<Record<number, number>>({});
  const [eventListingFee, setEventListingFee] = useState<number>(0);
  const [eventBoostLoading, setEventBoostLoading] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/my-events", { headers: authHeaders() });
      const data = await res.json() as { items: Event[] };
      setEvents(data.items ?? []);
    } catch { setEvents([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  useEffect(() => {
    if (!showForm) return;
    setEventBoostLoading(true);
    void Promise.all([
      fetch("/api/stripe/price?type=event", { headers: authHeaders() }).then(r => r.json() as Promise<{ price: number }>),
      ...([1, 2, 4] as const).map(w =>
        fetch(`/api/stripe/price?type=sponsored_event_${w}w`, { headers: authHeaders() })
          .then(r => r.json() as Promise<{ price: number }>)
          .then(d => [w, d.price] as const)
      ),
    ]).then(([listingRes, ...boostResults]) => {
      setEventListingFee((listingRes as { price: number }).price);
      setEventBoostPrices(Object.fromEntries(boostResults as [number, number][]));
      setEventBoostLoading(false);
    });
  }, [showForm]);

  // Handle return from Stripe payment for events
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("payment_success") === "1" && sessionId && params.get("type") === "event") {
      const savedForm = sessionStorage.getItem("pending_event_form");
      if (savedForm) {
        const formData = JSON.parse(savedForm) as typeof form & { sponsorWeeks?: 1|2|4|null };
        sessionStorage.removeItem("pending_event_form");
        window.history.replaceState({}, "", window.location.pathname);
        void fetch("/api/events", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ ...formData, stripeSessionId: sessionId }),
        }).then(async (r) => {
          if (!r.ok) {
            const d = await r.json().catch(() => ({})) as { error?: string };
            toast({ title: "Submission failed", description: d.error ?? "Payment was taken but your event could not be submitted. Please contact support.", variant: "destructive" });
            return;
          }
          setDone(true); void fetchEvents();
        }).catch(() => {
          toast({ title: "Submission failed", description: "Payment was taken but your event could not be submitted. Please contact support.", variant: "destructive" });
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const [listingData, ...boostArr] = await Promise.all([
        fetch("/api/stripe/price?type=event", { headers: authHeaders() }).then(r => r.json() as Promise<{ price: number; isFree: boolean }>),
        ...(eventSponsorWeeks ? [
          fetch(`/api/stripe/price?type=sponsored_event_${eventSponsorWeeks}w`, { headers: authHeaders() }).then(r => r.json() as Promise<{ price: number }>)
        ] : []),
      ]);
      const listingFee = listingData.price;
      const boostFee = eventSponsorWeeks ? (boostArr[0]?.price ?? 0) : 0;
      const totalAmount = listingFee + boostFee;

      if (totalAmount > 0) {
        sessionStorage.setItem("pending_event_form", JSON.stringify({ ...form, sponsorWeeks: eventSponsorWeeks }));
        const checkoutRes = await fetch("/api/stripe/checkout", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            amount: totalAmount / 100,
            description: eventSponsorWeeks
              ? `Event Listing + ${eventSponsorWeeks}w Featured Boost — Ayrshire Hub`
              : "Event Listing Fee — Ayrshire Hub",
            successUrl: `${window.location.origin}${window.location.pathname}?payment_success=1&type=event&session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: window.location.href,
            metadata: { type: "event", ...(eventSponsorWeeks ? { sponsorWeeks: String(eventSponsorWeeks) } : {}) },
          }),
        });
        const checkout = await checkoutRes.json() as { url?: string; error?: string };
        if (checkout.url) { window.location.href = checkout.url; return; }
        throw new Error(checkout.error ?? "Payment setup failed");
      }
      const eventRes = await fetch("/api/events", { method: "POST", headers: authHeaders(), body: JSON.stringify({ ...form, sponsorWeeks: eventSponsorWeeks }) });
      if (!eventRes.ok) { const d = await eventRes.json() as { error?: string }; throw new Error(d.error ?? "Failed to submit event"); }
      setDone(true);
      setShowForm(false);
      setForm({ name: "", description: "", category: "", town: "", venue: "", eventDate: "", eventTime: "", price: "", bookingLink: "", organiserName: "", imageUrl: "" });
      setEventSponsorWeeks(null);
      await fetchEvents();
    } finally { setSubmitting(false); }
  };

  const handleCancelEvent = async (id: number) => {
    if (!confirm("Cancel this event? It will be removed from the public listings.")) return;
    await fetch(`/api/events/${id}/cancel`, { method: "PATCH", headers: authHeaders() });
    await fetchEvents();
  };

  const startEditEvent = (ev: Event) => {
    setEditForm({ name: ev.name, description: ev.description ?? "", category: ev.category ?? "", town: ev.town ?? "Ayr", venue: ev.venue ?? "", eventDate: ev.eventDate ?? "", eventTime: ev.eventTime ?? "", price: ev.price ?? "", bookingLink: ev.bookingLink ?? "", organiserName: ev.organiserName ?? "", imageUrl: ev.imageUrl ?? "" });
    setEditingId(ev.id);
  };

  const handleSaveEdit = async (id: number) => {
    setEditSaving(true);
    try {
      const res = await fetch(`/api/events/${id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(editForm) });
      const data = await res.json() as { requiresReapproval?: boolean; error?: string };
      if (!res.ok) throw new Error(data.error ?? "Failed to save");
      if (data.requiresReapproval) alert("Event updated. As it was live, it has been sent for re-approval.");
      setEditingId(null);
      await fetchEvents();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Save failed");
    } finally { setEditSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-primary">My Events</h1>
        <Button onClick={() => setShowForm(!showForm)} className="bg-primary hover:bg-primary/90">
          {showForm ? "Cancel" : "+ New Event"}
        </Button>
      </div>

      {done && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg text-sm font-medium">
          Event submitted for approval. You'll be notified once it's reviewed.
        </div>
      )}

      {showForm && (
        <Card>
          <CardContent className="pt-6">
            <h2 className="font-semibold text-slate-800 mb-4">Submit New Event</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1 sm:col-span-2">
                  <label className="text-sm font-medium">Event Name *</label>
                  <Input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Category *</label>
                  <select required value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm">
                    <option value="">Select category</option>
                    {eventCats.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Town *</label>
                  <select required value={form.town} onChange={e => setForm(f => ({ ...f, town: e.target.value }))} className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm">
                    {TOWNS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Venue *</label>
                  <Input required value={form.venue} onChange={e => setForm(f => ({ ...f, venue: e.target.value }))} />
                </div>
                <BusinessPickerField
                  businesses={myBusinesses}
                  value={form.organiserName}
                  onChange={(name, town) => setForm(f => ({ ...f, organiserName: name, town: town || f.town }))}
                  label="Organiser (Business)"
                />
                <div className="space-y-1">
                  <label className="text-sm font-medium">Date *</label>
                  <Input type="date" required value={form.eventDate} onChange={e => setForm(f => ({ ...f, eventDate: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Time</label>
                  <Input type="time" value={form.eventTime} onChange={e => setForm(f => ({ ...f, eventTime: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Ticket Price</label>
                  <Input placeholder="e.g. Free or £10" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Booking Link</label>
                  <Input type="url" placeholder="https://..." value={form.bookingLink} onChange={e => setForm(f => ({ ...f, bookingLink: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Description *</label>
                <Textarea required rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Event Image</label>
                <ImageUpload value={form.imageUrl} onChange={url => setForm(f => ({ ...f, imageUrl: url }))} label="Upload Event Image" stockCategory="event" />
              </div>
              <div className="border border-amber-200 bg-amber-50 dark:bg-amber-900/20/60 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">⭐ Optional: Featured Boost</p>
                  <p className="text-xs text-amber-700 mt-0.5">Pin your event to the top of listings for extra visibility.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {([null, 1, 2, 4] as const).map(w => {
                    const boostPrice = w === null ? 0 : (eventBoostPrices[w] ?? 0);
                    const label = w === null ? "No boost" : w === 1 ? "1 Week" : w === 2 ? "2 Weeks" : "4 Weeks";
                    const boostLabel = eventBoostLoading ? "…" : w === null ? "" : boostPrice === 0 ? " · Free" : ` · £${(boostPrice / 100).toFixed(2)}`;
                    const active = eventSponsorWeeks === w;
                    return (
                      <button
                        key={String(w)}
                        type="button"
                        onClick={() => setEventSponsorWeeks(w)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${active ? (w === null ? "bg-slate-700 text-white border-slate-700" : "bg-amber-500 text-white border-amber-500") : "bg-white border-amber-200 text-amber-800 dark:text-amber-300 hover:border-amber-400"}`}
                      >
                        {label}{boostLabel}
                      </button>
                    );
                  })}
                </div>
                {!eventBoostLoading && (
                  <div className="text-xs text-amber-800 dark:text-amber-300 border-t border-amber-200 pt-2 space-y-0.5">
                    {eventListingFee > 0 && <div className="flex justify-between"><span>Listing fee</span><span className="font-medium">£{(eventListingFee / 100).toFixed(2)}</span></div>}
                    {eventSponsorWeeks && (eventBoostPrices[eventSponsorWeeks] ?? 0) > 0 && <div className="flex justify-between"><span>Boost ({eventSponsorWeeks}w)</span><span className="font-medium">£{((eventBoostPrices[eventSponsorWeeks] ?? 0) / 100).toFixed(2)}</span></div>}
                    {(eventListingFee > 0 || (eventSponsorWeeks && (eventBoostPrices[eventSponsorWeeks] ?? 0) > 0)) && (
                      <div className="flex justify-between font-semibold border-t border-amber-300 pt-1 mt-1">
                        <span>Total due today</span>
                        <span>£{((eventListingFee + (eventSponsorWeeks ? (eventBoostPrices[eventSponsorWeeks] ?? 0) : 0)) / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {eventListingFee === 0 && (!eventSponsorWeeks || (eventBoostPrices[eventSponsorWeeks] ?? 0) === 0) && (
                      <div className="text-emerald-700 font-medium">Free to submit</div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary/90">
                  {submitting ? "Submitting…" : eventSponsorWeeks ? "Submit + Boost" : "Submit for Approval"}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEventSponsorWeeks(null); }}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="space-y-3">{[1, 2].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">No events yet. Click "+ New Event" to submit your first event.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {events.map(ev => (
            <Card key={ev.id}>
              <CardContent className="py-4">
                {editingId === ev.id ? (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm text-slate-700">Edit Event</h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1 sm:col-span-2"><label className="text-xs font-medium text-slate-600">Name *</label><Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Category</label><select value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm"><option value="">Select</option>{eventCats.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Town</label><select value={editForm.town} onChange={e => setEditForm(f => ({ ...f, town: e.target.value }))} className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm">{TOWNS.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Venue</label><Input value={editForm.venue} onChange={e => setEditForm(f => ({ ...f, venue: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Date</label><Input type="date" value={editForm.eventDate} onChange={e => setEditForm(f => ({ ...f, eventDate: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Time</label><Input type="time" value={editForm.eventTime} onChange={e => setEditForm(f => ({ ...f, eventTime: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Ticket Price</label><Input placeholder="e.g. Free or £10" value={editForm.price} onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))} /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Booking Link</label><Input type="url" placeholder="https://..." value={editForm.bookingLink} onChange={e => setEditForm(f => ({ ...f, bookingLink: e.target.value }))} /></div>
                      <div className="space-y-1 sm:col-span-2"><label className="text-xs font-medium text-slate-600">Description</label><Textarea rows={3} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} /></div>
                    </div>
                    <div className="space-y-1"><label className="text-xs font-medium text-slate-600">Image</label><ImageUpload value={editForm.imageUrl} onChange={url => setEditForm(f => ({ ...f, imageUrl: url }))} label="Event Image" stockCategory="event" /></div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={() => void handleSaveEdit(ev.id)} disabled={editSaving}>{editSaving ? "Saving…" : "Save Changes"}</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3 items-start">
                    {ev.imageUrl && (
                      <img src={ev.imageUrl} alt={ev.name} className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <p className="font-semibold text-sm sm:text-base truncate">{ev.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{ev.category} · {ev.town} · {ev.venue}</p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {ev.eventDate && new Date(ev.eventDate).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                            {ev.eventTime && ` at ${ev.eventTime}`}
                            {ev.price && ` · ${ev.price}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                          {ev.sponsored && (
                            <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1">
                              ⭐ Sponsored
                              {ev.sponsoredUntil && <span className="font-normal opacity-80">until {new Date(ev.sponsoredUntil).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>}
                            </span>
                          )}
                          <StatusBadge approved={ev.approved} status={ev.status ?? undefined} />
                          <button onClick={() => startEditEvent(ev)} className="text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 hover:border-blue-400 px-2 py-1 rounded transition-colors">Edit</button>
                          {ev.approved && !ev.sponsored && (
                            <SponsorButton type="event" id={ev.id} onDone={fetchEvents} />
                          )}
                          {ev.approved && (
                            <button
                              onClick={() => handleCancelEvent(ev.id)}
                              className="text-xs text-red-500 hover:text-red-700 font-medium border border-red-200 hover:border-red-400 px-2 py-1 rounded transition-colors"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

interface MyFreeItem {
  id: number; title: string; description?: string | null;
  town: string; condition?: string | null; category?: string | null;
  price?: number | null; imageUrl?: string | null; collected?: boolean;
  createdAt?: string; sponsored?: boolean; sponsoredUntil?: string | null;
}

function MyMarketplace() {
  const [items, setItems] = useState<MyFreeItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/free-items/mine", { headers: authHeaders() });
      const data = await res.json() as { items: MyFreeItem[] };
      setItems(data.items ?? []);
    } catch { setItems([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { void fetchItems(); }, [fetchItems]);

  const isActiveBoost = (i: MyFreeItem) =>
    !!i.sponsored && (!i.sponsoredUntil || new Date(i.sponsoredUntil) > new Date());

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-primary">My Marketplace</h1>
          <p className="text-sm text-muted-foreground mt-1">Free / for-sale items you've posted on the marketplace.</p>
        </div>
        <Link href="/free-items"><Button variant="outline">Post New Item</Button></Link>
      </div>

      {loading ? (
        <div className="h-24 bg-slate-100 rounded-xl animate-pulse" />
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            You haven't listed any marketplace items yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Card key={item.id}>
              <CardContent className="py-4">
                <div className="flex items-start gap-3 flex-wrap">
                  {item.imageUrl && (
                    <img src={item.imageUrl} alt={item.title} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{item.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {item.town}
                      {item.condition ? ` · ${item.condition}` : ""}
                      {item.collected ? " · Collected" : ""}
                    </p>
                    {isActiveBoost(item) && (
                      <p className="text-xs text-amber-700 font-semibold mt-1">
                        ⭐ Boosted{item.sponsoredUntil ? ` until ${new Date(item.sponsoredUntil).toLocaleDateString()}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end flex-shrink-0">
                    {!item.collected && !isActiveBoost(item) && (
                      <SponsorButton type="free-item" id={item.id} onDone={fetchItems} />
                    )}
                    {isActiveBoost(item) && (
                      <span className="text-xs font-semibold text-amber-700 border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded">⭐ Boosted</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BusinessPortal() {
  const { user, isLoading } = useAuth();
  const { hasStore, storesLoaded } = usePendingOrders();

  if (isLoading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading...</div>;
  if (!user) return <Redirect to="/login" />;

  const isRoleAuthorized = user.role === "tradesperson" || user.role === "business" || user.role === "admin";

  // Wait for the stores fetch before denying a regular user — avoids a flash of the error screen
  if (!isRoleAuthorized && !storesLoaded) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading...</div>;
  }

  if (!isRoleAuthorized && !hasStore) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-muted-foreground">This portal is for registered trade accounts only.</p>
        <Link href="/"><Button variant="outline">Go Home</Button></Link>
      </div>
    );
  }

  return (
    <BusinessLayout>
      <Switch>
        <Route path="/business" component={BusinessOverview} />
        <Route path="/business/listings" component={MyBusinessListings} />
        <Route path="/business/profile" component={BusinessProfile} />
        <Route path="/business/leads" component={BusinessLeads} />
        <Route path="/business/deals" component={MyDeals} />
        <Route path="/business/events" component={MyEvents} />
        <Route path="/business/marketplace" component={MyMarketplace} />
        <Route path="/business/reviews" component={MyReviews} />
        <Route path="/business/submit-deal"><Redirect to="/business/deals" /></Route>
        <Route path="/business/submit-event"><Redirect to="/business/events" /></Route>
      </Switch>
    </BusinessLayout>
  );
}
